const zeroInsets = Object.freeze({ top: 0, right: 0, bottom: 0, left: 0 });

function finite(value, fallback = 0) {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
}

export function normalizeSafeArea(value = zeroInsets) {
  return {
    top: Math.max(0, finite(value.top)),
    right: Math.max(0, finite(value.right)),
    bottom: Math.max(0, finite(value.bottom)),
    left: Math.max(0, finite(value.left)),
  };
}

export function calculateViewportProfile({
  width,
  height,
  visualWidth = width,
  visualHeight = height,
  safeArea = zeroInsets,
  coarsePointer = false,
  orientation = null,
} = {}) {
  const fullWidth = Math.max(1, finite(width, 1));
  const fullHeight = Math.max(1, finite(height, 1));
  const visibleWidth = Math.max(1, Math.min(fullWidth, finite(visualWidth, fullWidth)));
  const visibleHeight = Math.max(1, Math.min(fullHeight, finite(visualHeight, fullHeight)));
  const insets = normalizeSafeArea(safeArea);
  const resolvedOrientation = orientation === "portrait" || orientation === "landscape"
    ? orientation
    : fullWidth >= fullHeight ? "landscape" : "portrait";
  const shortSide = Math.min(fullWidth, fullHeight);
  const longSide = Math.max(fullWidth, fullHeight);
  const phoneLike = Boolean(coarsePointer && shortSide <= 500 && longSide >= 560);
  const safeWidth = Math.max(1, visibleWidth - insets.left - insets.right);
  const safeHeight = Math.max(1, visibleHeight - insets.top - insets.bottom);
  const phoneLandscape = phoneLike && resolvedOrientation === "landscape";
  const mode = phoneLandscape
    ? (safeWidth <= 720 || safeHeight <= 380 ? "phone-compact" : "phone-wide")
    : "desktop";
  return {
    width: fullWidth,
    height: fullHeight,
    visualWidth: visibleWidth,
    visualHeight: visibleHeight,
    safeArea: insets,
    safeRect: {
      x: insets.left,
      y: insets.top,
      width: safeWidth,
      height: safeHeight,
      right: insets.left + safeWidth,
      bottom: insets.top + safeHeight,
    },
    orientation: resolvedOrientation,
    phoneLike,
    phoneLandscape,
    phoneCompact: mode === "phone-compact",
    mode,
    orientationBlocked: phoneLike && resolvedOrientation === "portrait",
  };
}

export function phoneWindowLayout(profile, topBarHeight = 52, margin = 8) {
  const safe = profile.safeRect;
  const x = safe.x + margin;
  const y = safe.y + topBarHeight + margin;
  return {
    x,
    y,
    width: Math.max(1, safe.width - margin * 2),
    height: Math.max(1, safe.bottom - y - margin),
  };
}

export function phoneCombatControlsLayout(profile, margin = 8) {
  const controlsHeight = Math.round(Math.min(240, Math.max(210, profile.safeRect.height * 0.58)));
  return {
    height: controlsHeight,
    y: profile.safeRect.bottom - controlsHeight - margin,
  };
}

export function clampToSafeRect(rect, safeRect, margin = 0) {
  const width = Math.min(Math.max(1, rect.width), Math.max(1, safeRect.width - margin * 2));
  const height = Math.min(Math.max(1, rect.height), Math.max(1, safeRect.height - margin * 2));
  return {
    x: Math.max(safeRect.x + margin, Math.min(rect.x, safeRect.right - width - margin)),
    y: Math.max(safeRect.y + margin, Math.min(rect.y, safeRect.bottom - height - margin)),
    width,
    height,
  };
}
